import { formatTrx } from "@tenergy/web-core/format";

/**
 * The scales the operator surface publishes, turned into something readable — and NOTHING
 * ELSE. Every function here takes the integer the API sent and says what it means; none of
 * them rounds a value the panel then sends back, because a form always posts the integer it
 * was given (`forms/mutation-form.tsx` shows both).
 *
 *   `_bp`        basis points of one SUN        29,000 = 2.9 SUN
 *   `_centi_sun` hundredths of one SUN           2,000 = 20.00 SUN
 *   `_sun`       whole SUN                     1e6 SUN = 1 TRX
 *   `_micro`     a ratio × 1e6                 500,000 = 50 %
 */

const NUMBER = new Intl.NumberFormat("en-US");

export function count(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : NUMBER.format(value);
}

/** Basis points of one SUN as SUN, to four decimals and no trailing noise. */
export function bpToSun(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${trim(value / 10_000)} SUN`;
}

/** Centi-SUN as SUN, at the 0.01 step pricing.md §1.3 works in. */
export function centiSunToSun(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${trim(value / 100)} SUN`;
}

/** Basis points as a percentage: 11,500 → "115.00 %" (a multiplier), 1,500 → "15.00 %". */
export function bps(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${trim(value / 100)} %`;
}

/** A ratio × 1e6 as a percentage. */
export function microPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${trim((value / 1_000_000) * 100)} %`;
}

export function trx(sun: number | null | undefined, digits = 3): string {
  if (sun === null || sun === undefined) return "—";
  return `${formatTrx(sun, digits)} TRX`;
}

export function sunAmount(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const asNumber = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(asNumber)) return String(value);
  return `${NUMBER.format(asNumber)} SUN`;
}

/**
 * Seconds as the unit a human reads them in. A latency of 0.043 s is "43 ms" and a latency of
 * 92 s is "92 s"; neither is "0.04" with the unit in the column head three rows up.
 */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
  if (seconds < 90) return `${trim(seconds, 2)} s`;
  return `${trim(seconds / 60, 1)} min`;
}

/**
 * A timestamp, in UTC, always. An admin panel is read by people in different places against
 * logs that are UTC and a chain whose day boundary is UTC; a local-time column here would be
 * the one thing in the whole stack that is not.
 */
export function utc(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return `${at.toISOString().slice(0, 16).replace("T", " ")}Z`;
}

export function utcDay(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return "—";
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toISOString().slice(0, 10);
}

/** Fixed to `maxDigits`, then trailing zeros and a bare point dropped: 2.9000 → 2.9, 3 → 3. */
function trim(value: number, maxDigits = 4): string {
  if (!Number.isFinite(value)) return String(value);
  const fixed = value.toFixed(maxDigits);
  if (!fixed.includes(".")) return fixed;
  return fixed.replace(/0+$/, "").replace(/\.$/, "");
}

/**
 * An ISO timestamp for an `<input type="datetime-local">`, in UTC.
 *
 * The control has no time zone, so the panel treats what is typed in it as UTC and says so
 * next to the field. Anything else would have an operator publish a price an hour early
 * because their laptop is on CET.
 */
export function toLocalInput(iso: string): string {
  return iso.slice(0, 16);
}

export function fromUtcInput(value: string): string | undefined {
  if (value.trim() === "") return undefined;
  const at = new Date(`${value}:00Z`);
  return Number.isNaN(at.getTime()) ? undefined : at.toISOString();
}
